/**
 * Offline queue for listen events (plan Lot C.2) — `push()` is called from
 * src/lib/audioPlayer.tsx on `start`/`complete`, `flush()` from main.tsx at
 * startup and on the `online` event.
 *
 * Persisted in localStorage under `chouette.listen.queue` rather than kept
 * in memory: a story is typically played with the screen locked or the app
 * backgrounded, so the event has to survive whatever happens to the tab
 * between "the child fell asleep mid-story" and "the parent reopens the app
 * days later with signal again".
 *
 * `enqueue` and `flushWith` are pure — the localStorage-touching wrappers
 * (`push`/`flush`) are thin shells around them, same split as
 * src/lib/installNudge.ts, and for the same reason: this repo's vitest runs
 * under `environment: "node"` (no jsdom), so only the pure decision logic is
 * unit-tested (see listenQueue.test.ts).
 */
import type { ApiCallResult, ListenEventKind } from "./types";
import { postListen } from "./dataSource";

export interface ListenQueueEntry {
  story_id: string;
  event: ListenEventKind;
  at: string;
}

const STORAGE_KEY = "chouette.listen.queue";

/** The local (device) calendar day an ISO timestamp falls on, e.g.
 *  "2026-09-24" — the dedup unit. `new Date(iso)`'s getters already read in
 *  local time, which is what "jour civil local" asks for, not the UTC day
 *  the ISO string's own digits might suggest. */
export function localDay(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dedupeKey(entry: ListenQueueEntry): string {
  return `${entry.story_id}:${entry.event}:${localDay(entry.at)}`;
}

/** Adds `entry` to `queue` unless one for the same (story_id, event, local
 *  day) is already queued. The backend upserts `start` and only ever sets
 *  `completed_at` once for `complete` (plan C.1: `UNIQUE(account_id,
 *  story_id, day)`), so a same-day repeat that slips through is harmless
 *  either way — this just keeps the local queue from piling up duplicates
 *  before it gets a chance to flush. */
export function enqueue(queue: ListenQueueEntry[], entry: ListenQueueEntry): ListenQueueEntry[] {
  const key = dedupeKey(entry);
  if (queue.some((e) => dedupeKey(e) === key)) return queue;
  return [...queue, entry];
}

export type ListenSender = (entry: ListenQueueEntry) => Promise<ApiCallResult>;

/** Sends every entry in `queue`, in order, via `sender`. Returns whatever
 *  must stay queued: a 204 (delivered) or 404 (the story is no longer
 *  readable — the plan says to drop it, not retry) removes an entry; a
 *  thrown network error, or any other status, keeps it for the next flush.
 *  Pure — no localStorage, no real network — so replay-after-failure and
 *  drop-on-404 are both directly testable. */
export async function flushWith(queue: ListenQueueEntry[], sender: ListenSender): Promise<ListenQueueEntry[]> {
  const remaining: ListenQueueEntry[] = [];
  for (const entry of queue) {
    try {
      const { status } = await sender(entry);
      if (status !== 204 && status !== 404) remaining.push(entry);
    } catch {
      remaining.push(entry);
    }
  }
  return remaining;
}

function readQueue(): ListenQueueEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isListenQueueEntry) : [];
  } catch {
    // Private browsing (Safari) can throw on `getItem` itself, and a
    // corrupted value would throw on `JSON.parse` — either way, an empty
    // queue is the safe fallback: a lost entry is a missed data point, never
    // a crash.
    return [];
  }
}

function writeQueue(queue: ListenQueueEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    /* best effort — private browsing etc. */
  }
}

function isListenQueueEntry(x: unknown): x is ListenQueueEntry {
  if (typeof x !== "object" || x === null) return false;
  const e = x as Record<string, unknown>;
  return typeof e.story_id === "string" && (e.event === "start" || e.event === "complete") && typeof e.at === "string";
}

/** Queue a listen event for later delivery. Called from audioPlayer.tsx on
 *  the first `play` and on reaching 90% / `ended` for the loaded track —
 *  never awaited, never throws: the whole point of the queue is to make a
 *  flaky or absent connection invisible to playback. */
export function push(entry: ListenQueueEntry): void {
  writeQueue(enqueue(readQueue(), entry));
}

let flushing = false;

/** Sends every queued entry (see `flushWith`) and persists whatever's left.
 *  Called at startup and on the `online` event (main.tsx). Re-entrant calls
 *  — e.g. a fast reconnect while a previous flush is still in flight — are
 *  dropped rather than queued: the next `online`/startup call picks up
 *  whatever is still there, so nothing is lost by skipping. */
export async function flush(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    const queue = readQueue();
    if (queue.length === 0) return;
    const remaining = await flushWith(queue, (entry) => postListen(entry.story_id, entry.event, entry.at));
    writeQueue(remaining);
  } finally {
    flushing = false;
  }
}
